import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Visibility from '@mui/icons-material/Visibility';
import { useExtensionWorkers } from '../hooks/useExtensionWorkers';
import {
  DefinitionTestSuiteItem,
  deleteDefinitionTestSuite,
  FieldCoverage,
  SuiteOutcome,
  useDefinitionTestSuite,
} from '../hooks/useDefinitionTestSuites';

type OutcomeFilter = 'all' | 'needs_work' | SuiteOutcome;

const ACCESS_LABEL: Record<string, string> = {
  stable: 'Stable',
  login_once: 'Login once',
  captcha_every_time: 'Captcha every time',
  needs_definition: 'Needs definition',
  unclassified: '—',
};

const OUTCOME_COLOR: Record<SuiteOutcome, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  passed: 'success',
  partial: 'warning',
  failed: 'error',
  skipped: 'default',
  pending: 'info',
};

function formatWhen(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function gapLabels(coverage: FieldCoverage | undefined, prefix = ''): string[] {
  if (!coverage?.sampled) return [];
  const empty = (coverage.empty || []).map(field => `${prefix}${field} (empty)`);
  const missing = (coverage.not_configured || []).map(
    field => `${prefix}${field} (not configured)`
  );
  return [...empty, ...missing];
}

function coverageTitle(coverage: FieldCoverage | undefined): string {
  if (!coverage?.sampled) return 'No listing was extracted for this stage';
  const lines = [
    coverage.present.length ? `Present: ${coverage.present.join(', ')}` : 'Present: none',
    ...gapLabels(coverage),
  ];
  return lines.join('\n');
}

function fieldGaps(item: DefinitionTestSuiteItem): string {
  return [...gapLabels(item.product_coverage), ...gapLabels(item.search_coverage, 'search ')].join(
    ', '
  );
}

function definitionEditPath(definitionId: string): string {
  return `/definitions/${definitionId}/edit`;
}

function SuiteResultRow({
  item,
  onResults,
}: {
  item: DefinitionTestSuiteItem;
  onResults: () => void;
}) {
  const gaps = fieldGaps(item);
  return (
    <TableRow hover>
      <TableCell>
        {item.marketplace_definition_id ? (
          <Tooltip title="Open marketplace definition">
            <Link
              component={RouterLink}
              to={definitionEditPath(item.marketplace_definition_id)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
              variant="body2"
              noWrap
              sx={{ display: 'inline-block', maxWidth: '100%', fontWeight: 600 }}
            >
              {item.marketplace_name}
            </Link>
          </Tooltip>
        ) : (
          <Typography variant="body2" noWrap>
            {item.marketplace_name}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {item.domain || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" label={item.outcome} color={OUTCOME_COLOR[item.outcome] || 'default'} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap>
          {ACCESS_LABEL[item.access_class || ''] || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={item.reason || 'No failure'}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
            {item.reason || '—'}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <CoverageCell coverage={item.search_coverage} />
      </TableCell>
      <TableCell>
        <CoverageCell coverage={item.product_coverage} />
      </TableCell>
      <TableCell>
        <Tooltip title={gaps || 'No field gaps on the sampled listing'}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
            {gaps || '—'}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Tooltip title="Open marketplace definition">
          <span>
            {item.marketplace_definition_id ? (
              <IconButton
                size="small"
                component={RouterLink}
                to={definitionEditPath(item.marketplace_definition_id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open marketplace definition"
              >
                <Edit fontSize="small" />
              </IconButton>
            ) : (
              <IconButton size="small" disabled aria-label="Open marketplace definition">
                <Edit fontSize="small" />
              </IconButton>
            )}
          </span>
        </Tooltip>
        <Tooltip title={item.task_id ? 'Task results' : 'No task'}>
          <span>
            <IconButton size="small" disabled={!item.task_id} onClick={onResults}>
              <Visibility fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={item.search_url ? 'Open search URL' : 'No search URL'}>
          <span>
            <IconButton
              size="small"
              disabled={!item.search_url}
              onClick={() => window.open(item.search_url, '_blank', 'noopener,noreferrer')}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function CoverageCell({ coverage }: { coverage?: FieldCoverage }) {
  if (!coverage?.sampled || !coverage.expected_count) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const pct = Math.round((coverage.present_count / coverage.expected_count) * 100);
  const color = pct === 100 ? 'success.main' : pct >= 50 ? 'warning.main' : 'error.main';
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{coverageTitle(coverage)}</Box>}>
      <Typography variant="body2" sx={{ color, cursor: 'help' }}>
        {coverage.present_count}/{coverage.expected_count}
      </Typography>
    </Tooltip>
  );
}

const DefinitionTestSuiteReport: React.FC = () => {
  const { suiteId = '' } = useParams();
  const navigate = useNavigate();
  const [live, setLive] = useState(true);
  const { data, isLoading, error, refetch } = useDefinitionTestSuite(suiteId, live);
  const { data: workers } = useExtensionWorkers(live);
  const [filter, setFilter] = useState<OutcomeFilter>('all');
  const [query, setQuery] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const userChoseFilter = useRef(false);

  useEffect(() => {
    if (!data) return;
    const stillRunning =
      data.status === 'preparing' || data.status === 'running' || (data.summary?.pending ?? 0) > 0;
    setLive(stillRunning);
  }, [data]);

  useEffect(() => {
    if (userChoseFilter.current || !data) return;
    if (data.status === 'completed' && data.summary.needs_work > 0) {
      setFilter('needs_work');
    }
  }, [data]);

  const chooseFilter = (next: OutcomeFilter) => {
    userChoseFilter.current = true;
    setFilter(next);
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (data?.items || [])
      .filter(item => {
        if (filter === 'needs_work' && !item.needs_work) return false;
        if (filter !== 'all' && filter !== 'needs_work' && item.outcome !== filter) return false;
        if (!needle) return true;
        return [item.marketplace_name, item.domain, item.reason, item.access_class]
          .join(' ')
          .toLowerCase()
          .includes(needle);
      })
      .sort((a, b) => a.marketplace_name.localeCompare(b.marketplace_name));
  }, [data?.items, filter, query]);

  const summary = data?.summary;
  const total = summary?.total ?? 0;
  const pending = summary?.pending ?? 0;
  const finished = Math.max(0, total - pending);
  const progress = total ? Math.round((finished / total) * 100) : 0;
  const running = data?.status === 'preparing' || data?.status === 'running';
  const onlineWorkers = workers?.summary?.online ?? 0;

  const chips: Array<{ id: OutcomeFilter; label: string; count?: number }> = [
    { id: 'all', label: 'All', count: total },
    { id: 'needs_work', label: 'Needs work', count: summary?.needs_work },
    { id: 'failed', label: 'Failed', count: summary?.failed },
    { id: 'partial', label: 'Partial', count: summary?.partial },
    { id: 'passed', label: 'Passed', count: summary?.passed },
    { id: 'skipped', label: 'Skipped', count: summary?.skipped },
    { id: 'pending', label: 'Pending', count: summary?.pending },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/test-suites')} sx={{ mb: 1 }}>
        Test suites
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" noWrap>
            {data ? `${data.brand_name} · ${data.keyword}` : 'Test suite'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.status === 'preparing'
              ? 'Queuing one extension task per marketplace…'
              : 'One keyword, one search page, one stage 2 product, Chrome extension.'}
            {data?.created_at ? ` Started ${formatWhen(data.created_at)}.` : ''}
          </Typography>
        </Box>
        {data && (
          <Chip
            label={data.status}
            color={running ? 'info' : data.status === 'failed' ? 'error' : 'success'}
          />
        )}
        <Button onClick={() => refetch()}>Refresh</Button>
        <Button
          color="error"
          startIcon={<Delete />}
          onClick={() => {
            setDeleteError(null);
            setDeleteOpen(true);
          }}
          disabled={!data || deleting}
        >
          Delete
        </Button>
      </Box>

      {running && (
        <LinearProgress
          sx={{ mb: 2 }}
          variant={total ? 'determinate' : 'indeterminate'}
          value={progress}
        />
      )}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load this test suite.
        </Alert>
      ) : null}
      {data?.error_message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {data.error_message}
        </Alert>
      )}
      {running && onlineWorkers === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No Chrome extension workers are online. Queued tasks will not run until a signed-in
          Collector extension is listening.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Passed means a stage 2 product came back with a title and URL. Empty fields are configured
        but were not extracted. Not configured means the definition has no selector for that field.
        Counts use the richest listing from the run. Access is how a priming worker should treat the
        marketplace: stable {summary?.stable ?? 0}, login once {summary?.login_once ?? 0}, captcha
        every time {summary?.captcha_every_time ?? 0}, needs a definition fix{' '}
        {summary?.needs_definition ?? 0}. Only stable and login-once hosts are worth scaling.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {chips.map(chip => (
          <Chip
            key={chip.id}
            label={chip.count === undefined ? chip.label : `${chip.label} ${chip.count}`}
            color={filter === chip.id ? 'primary' : 'default'}
            variant={filter === chip.id ? 'filled' : 'outlined'}
            onClick={() => chooseFilter(chip.id)}
          />
        ))}
        <TextField
          size="small"
          placeholder="Filter marketplaces"
          value={query}
          onChange={event => setQuery(event.target.value)}
          sx={{ ml: 'auto', minWidth: 220 }}
        />
      </Box>

      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Marketplace</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Access</TableCell>
              <TableCell>Why</TableCell>
              <TableCell>Search fields</TableCell>
              <TableCell>Product fields</TableCell>
              <TableCell>Field gaps</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8}>Loading results…</TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>No marketplaces in this filter.</TableCell>
              </TableRow>
            ) : (
              filtered.map(item => (
                <SuiteResultRow
                  key={item.id}
                  item={item}
                  onResults={() => navigate(`/tasks/${item.task_id}/results`)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete test suite?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the {data?.brand_name} run and its scraping tasks. Marketplace definitions
            are kept.
          </Typography>
          {deleteError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              if (!suiteId) return;
              setDeleting(true);
              setDeleteError(null);
              try {
                await deleteDefinitionTestSuite(suiteId);
                navigate('/test-suites');
              } catch (err: any) {
                setDeleteError(err?.response?.data?.detail || err?.message || 'Delete failed');
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DefinitionTestSuiteReport;
